import type { Unit } from "@/api/raptor";
import { useApi, useAuth, useGetAll } from "@mb-pro-ui/utils";
import { PlayArrow, Work } from "@mui/icons-material";
import LogoutIcon from "@mui/icons-material/Logout";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useLocation } from "wouter";

export function ShiftSelector() {
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const api = useApi();
  const auth = useAuth();

  const [selectedUnit, setSelectedUnit] = useState<Unit | null>(null);

  const { data, isLoading } = useGetAll<Unit>("raptor/units", {
    fields: { units: ["name", "active"] },
    filter: { active: { is: "true" } },
  });

  const [_, navigate] = useLocation();

  const { mutate: startSession, isPending: isStarting } = useMutation({
    mutationFn: (unitId: string) =>
      api(`/actions/raptor/units/${unitId}/start-session`, { method: "POST" }),
    onSuccess: () => {
      if (selectedUnit) {
        navigate(`/jobs`);
      }
    },
    onError: (err) => {
      console.error("Hiba a műszak indításakor:", err);
    },
  });

  return (
    <Container
      maxWidth="sm"
      disableGutters
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        px: 2,
        py: 3,
      }}
    >
      {/* Header */}
      <Box sx={{ textAlign: "center", mb: 3 }}>
        <Box
          sx={{
            width: 70,
            height: 70,
            mx: "auto",
            mb: 2,
            background: "linear-gradient(135deg, #0e4791ff 0%, #4386aca1 100%)",
            borderRadius: "24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 0px 16px rgba(141, 141, 141, 0.72)",
          }}
        >
          <Work sx={{ fontSize: 48, color: "white" }} />
        </Box>
        <Typography variant="h5" sx={{ color: "#5a5a5a", mb: 1 }}>
          {formatMessage({ defaultMessage: "Shift start" })}
        </Typography>
        <Typography variant="body1" sx={{ color: "#616161" }}>
          {formatMessage({
            defaultMessage: "Select the dispatch unit to start the shift",
          })}
        </Typography>
      </Box>

      {isLoading && (
        <Box
          color="grey.400"
          sx={{ display: "grid", placeItems: "center", flex: 1 }}
        >
          <CircularProgress color="inherit" />
        </Box>
      )}

      {/* Lista (rugalmas doboz) */}
      <Box
        sx={{
          flexShrink: 1,
          overflow: "auto",
          mb: 3,
          maxHeight: "40vh", // reszponzív korlát
          "-webkit-overflow-scrolling": "touch",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            overflow: (data?.length ?? 0) > 5 ? "auto" : "hidden",
            "-webkit-overflow-scrolling": "touch",
            background: "#F5F5F5",
          }}
        >
          <List disablePadding>
            {data?.map((unit, index) => (
              <ListItem key={unit.id} disablePadding>
                <ListItemButton
                  selected={selectedUnit?.id === unit.id}
                  onClick={() => setSelectedUnit(unit)}
                  sx={{
                    py: 1.5,
                    px: 1.5,
                    borderBottom:
                      index < data.length - 1 ? "1px solid #E0E0E0" : "none",
                    pl: 1.875,
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      bottom: 0,
                      width: "3px",
                      bgcolor:
                        selectedUnit?.id === unit.id
                          ? (theme) => theme.palette.primary.main
                          : "transparent",
                    },
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color="#5a5a5a"
                        sx={{ pt: 1, pb: 1 }}
                      >
                        {unit.name}
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
            )) ?? null}
          </List>
        </Paper>
      </Box>

      {/* Gomb legalul */}
      <Button
        variant="contained"
        size="large"
        fullWidth
        disabled={!selectedUnit || isStarting}
        onClick={() => {
          if (selectedUnit) startSession(selectedUnit.id);
        }}
        startIcon={<PlayArrow />}
        sx={{
          mt: "auto",
          py: 1.5,
          fontWeight: 500,
          background: selectedUnit
            ? "linear-gradient(135deg, #0e4791ff 0%, #4386acb0 100%)"
            : "#e5e7eb",
          boxShadow: selectedUnit
            ? "0 0px 16px rgba(141, 141, 141, 0.72)"
            : "none",
          "&:disabled": {
            background: "#e4e4e4ff",
            color: "#9E9E9E",
          },
        }}
      >
        {formatMessage({ defaultMessage: "Shift start" })}
      </Button>

      <Button
        color="primary"
        fullWidth
        startIcon={<LogoutIcon />}
        onClick={() => {
          auth.signout.mutate(undefined, {
            onSuccess: () => {
              queryClient.clear();
              navigate("/login", { replace: true });
            },
          });
        }}
        sx={{ mt: 3 }}
      >
        <Typography variant="raptor-button-text" color="primary">
          {formatMessage({ defaultMessage: "Logout" })}{" "}
        </Typography>{" "}
      </Button>
    </Container>
  );
}
